import { useEffect, useRef } from 'react';
import { Routes, Route } from 'react-router-dom';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { Products } from '@/pages/Products';
import { ProductDetail } from '@/pages/ProductDetail';
import { Cart } from '@/pages/Cart';
import { Checkout } from '@/pages/Checkout';
import { Login } from '@/pages/Login';
import { Register } from '@/pages/Register';
import { AppHome } from '@/pages/AppHome';
import { Orders } from '@/pages/Orders';
import { OrderDetails } from '@/pages/OrderDetails';
import { OrderTracking } from '@/pages/OrderTracking';
import { Account } from '@/pages/Account';
import { Address } from '@/pages/Address';
import { Coupons } from '@/pages/Coupons';
import { Wishlist } from '@/pages/Wishlist';
import { HelpCenter } from '@/pages/Account/Help';
import { RefundPolicy } from '@/pages/Account/Refund';
import { PrivacyPolicy } from '@/pages/Account/Privacy';
import { CustomerProfile } from '@/pages/Customer/Profile';
import { CustomerLayout } from '@/components/layouts/CustomerLayout';
import { useAuthStore, initializeAuth } from '@/store/authStore';
import { useOrderStore } from '@/store/orderStore';
import { useProductStore } from '@/store/productStore';
import { useRefreshStore } from '@/store/refreshStore';
import { supabase, isSupabaseConfigured } from '@/utils/supabase';
import { useAblyOrders } from '@/hooks/useAblyOrders';
import { Toaster } from '@/components/ui/sonner';
import { toast } from 'sonner';

// [SUPREME SOUND RELIABILITY]
let sharedAudioContext: AudioContext | null = null;

const playNotificationSound = (volume = 1.0) => {
  try {
    if (!sharedAudioContext) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const AudioContext = window.AudioContext || (window as any).webkitAudioContext;
      if (!AudioContext) return;
      sharedAudioContext = new AudioContext();
    }
    const context = sharedAudioContext;
    if (context.state === 'suspended') {
      context.resume();
    }
    const playTone = (freq: number, start: number, duration: number) => {
      const osc = context.createOscillator();
      const gain = context.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(freq, start);
      gain.gain.setValueAtTime(0, start);
      gain.gain.linearRampToValueAtTime(volume, start + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.01, start + duration);
      osc.connect(gain);
      gain.connect(context.destination);
      osc.start(start);
      osc.stop(start + duration);
    };
    playTone(660, context.currentTime, 0.4);
    playTone(440, context.currentTime + 0.1, 0.5);
  } catch (err) {
    console.error('[Sound] WebAudio fallback failed:', err);
  }
};

export default function CustomerApp() {
  const { user, profile } = useAuthStore();
  const {
    initRealtimeSubscription,
    cleanupRealtimeSubscription,
    newOrderTick
  } = useOrderStore();

  const {
    initRealtimeSubscription: initProductSubscription,
    cleanupRealtimeSubscription: cleanupProductSubscription
  } = useProductStore();

  const previousTick = useRef(0);
  const { hardRefresh } = useRefreshStore();

  // Ably order tracking for customers
  useAblyOrders(user?.id || '', profile?.role || '');

  useEffect(() => {
    if (isSupabaseConfigured) {
      void initializeAuth();
    }
  }, []);

  // Global Visibility Listener for Auto-Refresh
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void hardRefresh();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleVisibilityChange);
    };
  }, [hardRefresh]);

  // Supabase Realtime (fallback) + Ably order tracking
  useEffect(() => {
    if (!isSupabaseConfigured || !user || !profile?.role) return;

    const syncAll = () => {
      if (document.visibilityState === 'visible') {
        void initRealtimeSubscription();
        void initProductSubscription();
      }
    };

    initRealtimeSubscription();
    initProductSubscription();

    // Ably automatically subscribes when mounted based on user details

    window.addEventListener('online', syncAll);
    window.addEventListener('visibilitychange', syncAll);
    window.addEventListener('focus', syncAll);

    const keepAlive = setInterval(async () => {
      if (isSupabaseConfigured) {
        await supabase.from('profiles').select('id').limit(1);
      }
    }, 300000);

    return () => {
      cleanupRealtimeSubscription();
      cleanupProductSubscription();
      window.removeEventListener('online', syncAll);
      window.removeEventListener('visibilitychange', syncAll);
      window.removeEventListener('focus', syncAll);
      clearInterval(keepAlive);
    };
  }, [
    user,
    user?.id,
    profile?.role,
    initRealtimeSubscription,
    initProductSubscription,
    cleanupRealtimeSubscription,
    cleanupProductSubscription
  ]);

  // Global Realtime Notification Listener
  const { isSoundEnabled } = useOrderStore();
  useEffect(() => {
    if (newOrderTick > previousTick.current && profile?.role && profile.role !== 'customer') {
      toast.success('🔔 New Order Alert!', {
        description: 'A new order has arrived or been assigned to you.',
        duration: 6000,
        position: 'top-right',
      });
      if (isSoundEnabled) playNotificationSound(0.4);
      previousTick.current = newOrderTick;
    }
  }, [newOrderTick, profile?.role, isSoundEnabled]);

  return (
    <>
      <Routes>
        <Route element={<CustomerLayout />}>
          <Route path="/" element={<AppHome />} />
          <Route path="/index.html" element={<AppHome />} />
          <Route path="/products" element={<Products />} />
          <Route path="/products/:id" element={<ProductDetail />} />
          <Route path="/product/:id" element={<ProductDetail />} />
          <Route path="/cart" element={<Cart />} />
          <Route path="/checkout" element={<ProtectedRoute><Checkout /></ProtectedRoute>} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/orders" element={<ProtectedRoute><Orders /></ProtectedRoute>} />
          <Route path="/orders/:id" element={<ProtectedRoute><OrderDetails /></ProtectedRoute>} />
          <Route path="/orders/:id/track" element={<ProtectedRoute><OrderTracking /></ProtectedRoute>} />
          <Route path="/account" element={<ProtectedRoute><Account /></ProtectedRoute>} />
          <Route path="/address" element={<ProtectedRoute><Address /></ProtectedRoute>} />
          <Route path="/coupons" element={<ProtectedRoute><Coupons /></ProtectedRoute>} />
          <Route path="/wishlist" element={<ProtectedRoute><Wishlist /></ProtectedRoute>} />
          <Route path="/account/help" element={<HelpCenter />} />
          <Route path="/account/refund" element={<RefundPolicy />} />
          <Route path="/account/privacy" element={<PrivacyPolicy />} />
          <Route path="/profile" element={<ProtectedRoute><CustomerProfile /></ProtectedRoute>} />
          <Route path="*" element={<AppHome />} />
        </Route>
      </Routes>
      <Toaster position="top-center" />
    </>
  );
}
